import React, { useEffect, useState } from 'react'
import {
    Grid,
    Box,
    Typography,
    Card
} from "@mui/material"
import axios from 'axios'
import { toastError, getObject } from '../../utils'
import FronicLogo from '../../assets/img/Fronic_Logo_white.png'

// Ripple animation for live indicator
const rippleAnimation = `
    @keyframes pulse-dot {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.2); opacity: 0.7; }
    }

    @keyframes ripple {
        0% {
            transform: scale(0.8);
            opacity: 1;
        }
        100% {
            transform: scale(2.5);
            opacity: 0;
        }
    }
`

export default function LiveAnalytics() {
    const [analyticsData, setAnalyticsData] = useState({
        total_online_users: 0,
        online_users_list: [],
        pages: []
    })
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        // Set body styles for full-screen experience
        document.body.style.margin = '0'
        document.body.style.padding = '0'
        document.body.style.overflow = 'hidden'

        fetchAnalytics()
        // Auto-refresh every 30 seconds
        const interval = setInterval(() => {
            fetchAnalytics()
        }, 30000)

        return () => {
            // Clean up body styles on unmount
            document.body.style.margin = ''
            document.body.style.padding = ''
            document.body.style.overflow = ''
            clearInterval(interval)
        }
    }, [])

    const fetchAnalytics = () => {
        setLoading(true)
        const auth = getObject("auth")

        axios.get('https://backend.fronicmedia.com/api/getOnlineUsersAdmin', {
            headers: {
                Authorization: auth
            }
        })
            .then(response => {
                if (response.data.status === 200) {
                    setAnalyticsData(response.data.body)
                }
                setLoading(false)
            })
            .catch(err => {
                toastError(err?.message || 'Failed to fetch analytics');
                setLoading(false)
            })
    }

    return (
        <>
            {/* Inject ripple animation */}
            <style>{rippleAnimation}</style>

            <Box style={{
                position: 'fixed',
                top: 0,
                left: 0,
                padding: '1.5%',
                height: '100vh',
                width: '100vw',
                background: '#0a0a0a',
                color: '#fff',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box'
            }}>
                {/* Header */}
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ marginBottom: '1%', minHeight: '8%' }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <img
                            src={FronicLogo}
                            alt="Fronic Media"
                            style={{
                                height: 'clamp(30px, 5vh, 60px)',
                                width: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                        <Typography style={{ fontSize: 'clamp(20px, 3vh, 40px)', fontWeight: 700, color: '#fff', letterSpacing: '1px' }}>
                            Live Analytics
                        </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Typography style={{ fontSize: 'clamp(12px, 1.5vh, 20px)', color: '#b0b0b0', fontWeight: 500 }}>
                            Updating live
                        </Typography>

                        {/* Red pulsing dot with ripple waves */}
                        <Box
                            style={{
                                position: 'relative',
                                width: '1.2vh',
                                height: '1.2vh',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {/* Ripple wave 1 */}
                            <Box
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    border: '0.15vh solid #f44336',
                                    animation: 'ripple 1.5s ease-out infinite',
                                    animationDelay: '0s'
                                }}
                            />
                            {/* Ripple wave 2 */}
                            <Box
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    border: '0.15vh solid #f44336',
                                    animation: 'ripple 1.5s ease-out infinite',
                                    animationDelay: '0.5s'
                                }}
                            />
                            {/* Ripple wave 3 */}
                            <Box
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    border: '0.15vh solid #f44336',
                                    animation: 'ripple 1.5s ease-out infinite',
                                    animationDelay: '1s'
                                }}
                            />
                            {/* Center red dot */}
                            <Box
                                style={{
                                    width: '1.2vh',
                                    height: '1.2vh',
                                    backgroundColor: '#f44336',
                                    borderRadius: '50%',
                                    animation: 'pulse-dot 1.5s ease-in-out infinite',
                                    boxShadow: '0 0 1vh rgba(244, 67, 54, 0.5)',
                                    zIndex: 1
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

            {/* Main Stats - Users Online */}
            <Card
                elevation={0}
                style={{
                    background: '#1a1a1a',
                    padding: '2%',
                    textAlign: 'center',
                    borderRadius: '12px',
                    marginBottom: '1.5%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    border: '1px solid #2a2a2a',
                    minHeight: '18%',
                    maxHeight: '20%'
                }}
            >
                <Typography style={{ fontSize: 'clamp(14px, 1.8vh, 24px)', color: '#b0b0b0', fontWeight: 600, marginBottom: '1%', letterSpacing: '2px' }}>
                    USERS ONLINE NOW
                </Typography>
                <Box display="flex" justifyContent="center" alignItems="center">
                    <Typography
                        style={{
                            fontSize: 'clamp(40px, 10vh, 120px)',
                            color: '#10b981',
                            fontWeight: 900,
                            lineHeight: 1,
                            textShadow: '0 0 30px rgba(16, 185, 129, 0.3)'
                        }}
                    >
                        {analyticsData.total_online_users}
                    </Typography>
                </Box>
            </Card>

            {/* Page Activity Section */}
            <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                <Typography style={{ fontSize: 'clamp(14px, 1.8vh, 24px)', fontWeight: 600, color: '#b0b0b0', textAlign: 'center', marginBottom: '1%', letterSpacing: '2px' }}>
                    PAGE ACTIVITY
                </Typography>
                {analyticsData.pages.length > 0 ? (
                    <Box
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
                            gridAutoRows: '1fr',
                            gap: '1.5%',
                            flex: 1,
                            overflow: 'hidden',
                            alignContent: 'start'
                        }}
                    >
                        {analyticsData.pages
                            .sort((a, b) => b.active_users_count - a.active_users_count)
                            .map((page, index) => (
                                <Card
                                    key={index}
                                    elevation={0}
                                    style={{
                                        background: '#1a1a1a',
                                        border: page.active_users_count > 0
                                            ? '2px solid #10b981'
                                            : '1px solid #2a2a2a',
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        transition: 'all 0.3s ease',
                                        height: '100%',
                                        boxShadow: page.active_users_count > 0
                                            ? '0 0 20px rgba(16, 185, 129, 0.2)'
                                            : 'none',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Typography
                                        style={{
                                            color: '#e0e0e0',
                                            fontWeight: 600,
                                            fontSize: 'clamp(11px, 1.4vh, 18px)',
                                            marginBottom: '8px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            minHeight: 'clamp(22px, 2.8vh, 36px)'
                                        }}
                                    >
                                        {page.page_name}
                                    </Typography>
                                    <Box textAlign="center">
                                        <Typography
                                            style={{
                                                fontSize: 'clamp(28px, 4.5vh, 70px)',
                                                color: page.active_users_count > 0 ? '#10b981' : '#666',
                                                fontWeight: 900,
                                                lineHeight: 1,
                                                textShadow: page.active_users_count > 0
                                                    ? '0 0 20px rgba(16, 185, 129, 0.3)'
                                                    : 'none'
                                            }}
                                        >
                                            {page.active_users_count}
                                        </Typography>
                                        <Typography
                                            style={{
                                                fontSize: 'clamp(9px, 1.1vh, 16px)',
                                                color: page.active_users_count > 0 ? '#b0b0b0' : '#555',
                                                fontWeight: 500,
                                                marginTop: '4px',
                                                letterSpacing: '1px'
                                            }}
                                        >
                                            {page.active_users_count === 1 ? 'USER' : 'USERS'}
                                        </Typography>
                                    </Box>
                                </Card>
                            ))}
                    </Box>
                ) : (
                    <Box textAlign="center" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography style={{ fontSize: 'clamp(16px, 2vh, 28px)', color: '#666' }}>
                            No page data available
                        </Typography>
                    </Box>
                )}
            </Box>

            </Box>
        </>
    )
}
